import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import OrganizationFunctionsService from "src/modules/data-protection/apis/OrganizationFunctionsService";
import {
    ORGANIZATION_FUNCTIONS_LIST_QUERY_KEY,
    ORGANIZATION_FUNCTION_ERROR_CODES,
    getOrganizationFunctionErrorCode,
    getOrganizationFunctionQueryKey,
    toastOrganizationFunctionError,
} from "src/modules/data-protection/constants/organizationFunctions";
import { ROUTE_PATH_ADMIN_USER_LIST } from "src/modules/data-protection/constants";

const FUNCTIONS_LIST_PATH = `${ROUTE_PATH_ADMIN_USER_LIST}?tab=functions`;

const useOrganizationFunctionMutations = ({ functionKey, t } = {}) => {
    const queryClient = useQueryClient();
    const history = useHistory();

    const invalidateFunctionQueries = () => {
        if (functionKey) {
            queryClient.invalidateQueries(
                getOrganizationFunctionQueryKey(functionKey)
            );
        }

        queryClient.invalidateQueries(ORGANIZATION_FUNCTIONS_LIST_QUERY_KEY);
    };

    const updateMutation = useMutation({
        mutationFn: async (payload) => {
            const service = OrganizationFunctionsService.getInstance();
            return await service.update(functionKey, payload);
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            toastOrganizationFunctionError(
                error,
                t,
                "An error occurred while saving the Function."
            );
        },
    });

    const assignMutation = useMutation({
        mutationFn: async (userId) => {
            const service = OrganizationFunctionsService.getInstance();
            return await service.assign(functionKey, userId);
        },
        onSuccess: () => {
            invalidateFunctionQueries();
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            const code = getOrganizationFunctionErrorCode(error);

            if (code !== ORGANIZATION_FUNCTION_ERROR_CODES.DPO_ALREADY_ASSIGNED) {
                toastOrganizationFunctionError(
                    error,
                    t,
                    "An error occurred while assigning the user."
                );
            }
        },
    });

    const replaceDpoMutation = useMutation({
        mutationFn: async (userId) => {
            const service = OrganizationFunctionsService.getInstance();
            return await service.replaceDpo(userId);
        },
        onSuccess: () => {
            invalidateFunctionQueries();
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            toastOrganizationFunctionError(
                error,
                t,
                "An error occurred while replacing the DPO."
            );
        },
    });

    const unassignMutation = useMutation({
        mutationFn: async (userId) => {
            const service = OrganizationFunctionsService.getInstance();
            return await service.unassign(functionKey, userId);
        },
        onSuccess: () => {
            invalidateFunctionQueries();
            toast(t("Assignment removed successfully."), {
                type: "success",
            });
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            toastOrganizationFunctionError(
                error,
                t,
                "An error occurred while removing the assignment."
            );
        },
    });

    const removeMutation = useMutation({
        mutationFn: async () => {
            const service = OrganizationFunctionsService.getInstance();
            return await service.remove(functionKey);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(ORGANIZATION_FUNCTIONS_LIST_QUERY_KEY);
            toast(t("Function deleted successfully."), {
                type: "success",
            });
            history.push(FUNCTIONS_LIST_PATH);
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            toastOrganizationFunctionError(
                error,
                t,
                "An error occurred while deleting the Function."
            );
        },
    });

    return {
        updateMutation,
        assignMutation,
        replaceDpoMutation,
        unassignMutation,
        removeMutation,
        invalidateFunctionQueries,
    };
};

export default useOrganizationFunctionMutations;
